import { FC, PropsWithChildren, useCallback, useContext, useMemo } from 'react';

import { Image } from '@rbilabs/universal-components';
import { useRouter } from 'expo-router';
import { logButtonClick } from 'features/navigation/utils/analytics';
import { GestureResponderEvent, Linking } from 'react-native';

import { Card } from 'components/ucl/card';
import { ClickEventComponentNames, CustomEventNames, EventTypes, logEvent } from 'state/amplitude';
import { BrazeContext } from 'state/braze';
import { CONTENT_CARD_SETTING } from 'state/braze/types';
import { t } from 'state/intl/translations';
import { isExternalLink } from 'utils/is-external-link';

import useTerms from '../terms/terms.hook';

import { IBrazeMarketingCardProps } from './types';
import { termsToSanityBlockContent, validateHexColor } from './utils';

/**
 *
 * The BrazeBrazeMarketingCard component displays cards from Braze Content Card campaigns on the homepage.
 *
 */
export const BrazeMarketingCard: FC<PropsWithChildren<IBrazeMarketingCardProps>> = ({
  card,
  onShowTermsModal,
  index,
  dismissInboxBeforeNavigation = false,
  isUnread,
}) => {
  const { dismiss, navigate } = useRouter();

  const { logContentCardImpression, logContentCardClicked } = useContext(BrazeContext);

  const { extras = {} } = card;

  // Colors
  const isCustom = extras?.[CONTENT_CARD_SETTING.campaign] === 'true';
  const variant = isCustom ? 'campaign' : 'default';
  const buttonTextColor = validateHexColor(extras[CONTENT_CARD_SETTING.buttonTextColor]);
  const buttonBgColor = validateHexColor(extras[CONTENT_CARD_SETTING.buttonBackgroundColor]);
  const bgColor = validateHexColor(extras[CONTENT_CARD_SETTING.backgroundColor]);
  const textColor = validateHexColor(extras[CONTENT_CARD_SETTING.textColor]);
  // Call to action
  const callToAction = card.domain ?? '';
  const href = card.url ?? '';

  // Content
  const header = card.title;
  const description = card.cardDescription;
  // If content team doesn't write an image description try to use the card title
  // as description of the image link to ensure context for accessibility users.
  const imageAlt = extras[CONTENT_CARD_SETTING.imageAlt] ?? header;

  const onImageLoaded = useCallback(() => {
    if (!card.viewed) {
      // Log to Braze
      logContentCardImpression(card.id);
    }
  }, [card, logContentCardImpression]);

  const image = useMemo(
    () => (
      <Image
        accessibilityIgnoresInvertColors
        resizeMode="cover"
        source={{
          uri: card.image,
        }}
        alt={imageAlt}
        borderTopRadius={16}
        width="full"
        style={{ aspectRatio: 2 }}
        onLoad={onImageLoaded}
      />
    ),
    [card.image, imageAlt, onImageLoaded]
  );

  const logDataEvent = useCallback(() => {
    logButtonClick({
      attributes: {
        component: ClickEventComponentNames.MARKETING_TILE,
        componentId: card.id,
        name: header,
        position: `Tile ${index + 1}`,
        cardType: 'braze-content-card',
        ...(isUnread !== undefined && { isUnread }),
      },
    });

    const eventAttributes = {
      'Marketing Card ID': card.extras?.[CONTENT_CARD_SETTING.campaignId] || card.id,
      'Marketing Card URL': href,
      brazeId: card.id,
      marketingCardUrl: href,
      marketingTileName: header,
      ...(isUnread !== undefined && { isUnread }),
    };
    logEvent(CustomEventNames.MARKETING_TILE_CLICK, EventTypes.Navigation, eventAttributes);

    logContentCardClicked(card, false);
  }, [card, header, index, href, isUnread, logContentCardClicked]);

  const onPress = useCallback(() => {
    logDataEvent();
    // Prevent navigating to an empty url
    if (!href) {
      return;
    }
    if (isExternalLink(href)) {
      Linking.openURL(href);
      return;
    }
    // Leave the notification inbox drawer before in-app navigation.
    if (dismissInboxBeforeNavigation) {
      dismiss();
    }
    navigate(href);
  }, [dismiss, dismissInboxBeforeNavigation, href, logDataEvent, navigate]);

  // Terms
  const termsAccessibilityLabel = t('termsAccessibilityLabel', { cardTitle: header });
  const inlineTerms = extras[CONTENT_CARD_SETTING.terms];
  const hasInlineTerms = !!inlineTerms;
  const termsButtonUrl = extras[CONTENT_CARD_SETTING.termsButtonActionUrl];
  const hasTerms = hasInlineTerms || !!termsButtonUrl;
  const termsButtonText =
    extras[CONTENT_CARD_SETTING.termsButtonActionText] ?? t('termsConditions');
  const termsLinkHref = hasTerms ? termsButtonUrl : undefined;
  const termsLinkText = hasTerms ? termsButtonText : undefined;
  const termsText = hasInlineTerms ? termsToSanityBlockContent(inlineTerms) : [];

  const onPressTermsLinkFromHook = useTerms({
    onShowTermsModal,
    termsButtonText,
    termsButtonUrl: termsButtonUrl ?? '',
    termsText,
  });

  const onPressTermsLink = useCallback(
    (event?: GestureResponderEvent) => {
      if (termsButtonUrl && !hasInlineTerms) {
        if (dismissInboxBeforeNavigation) {
          dismiss();
        }
        return;
      }

      onPressTermsLinkFromHook(event as any);
    },
    [
      dismiss,
      dismissInboxBeforeNavigation,
      hasInlineTerms,
      onPressTermsLinkFromHook,
      termsButtonUrl,
    ]
  );

  return (
    <Card
      href={href}
      variant={variant}
      header={header}
      description={description}
      image={image}
      callToAction={callToAction}
      onPress={onPress}
      buttonTextColor={buttonTextColor}
      buttonBgColor={buttonBgColor}
      bgColor={bgColor}
      textColor={textColor}
      // Terms
      termsAccessibilityLabel={termsAccessibilityLabel}
      termsLinkHref={termsLinkHref}
      termsLinkText={termsLinkText}
      onPressTermsLink={onPressTermsLink as any}
    />
  );
};
